import type { Handler } from './handler';
import type { ApiFetchHomeSectionsPort } from './ports/api-fetch-home-sections.port';
import homeSectionsFallback from '~/assets/json/home-sections.fallback.json';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import { resolveAdapter } from '~/adapters/registry';

const CACHE_TTL_MS = 5 * 60 * 1000;
const RETRY_DELAY_MS = 250;

let lastGoodResponse: HomeSectionData | HomeSection | null = null;
let lastGoodAt = 0;

// Aceita só payloads com dados de fato — `{items: []}` ou `{categories: []}`
// não devem mascarar uma resposta vazia da API.
const isValidHomeSection = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;

  const v = value as { items?: unknown; categories?: unknown };

  if (Array.isArray(v.items) && v.items.length > 0) return true;
  if (Array.isArray(v.categories) && v.categories.length > 0) return true;

  return false;
};

async function fetchOnceWithRetry(
  port: ApiFetchHomeSectionsPort,
): Promise<HomeSectionData | HomeSection | null> {
  const [first, firstErr] = await tryCatch(port.handle());

  if (!firstErr && isValidHomeSection(first)) {
    return first as HomeSectionData | HomeSection;
  }

  await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

  const [second, secondErr] = await tryCatch(port.handle());

  if (!secondErr && isValidHomeSection(second)) {
    return second as HomeSectionData | HomeSection;
  }

  if (secondErr) {
    throw secondErr;
  }

  return null;
}

export const fetchHomeSectionsHandler: Handler<Params, Ports, Result> = async (
  ports = {
    apiFetchHomeSections: resolveAdapter('apiFetchHomeSections'),
  },
) => {
  if (lastGoodResponse && Date.now() - lastGoodAt < CACHE_TTL_MS) {
    return { homeSection: lastGoodResponse };
  }

  const [homeSection, fetchError] = await tryCatch(
    fetchOnceWithRetry(ports!.apiFetchHomeSections),
  );

  if (!fetchError && homeSection) {
    lastGoodResponse = homeSection;
    lastGoodAt = Date.now();

    return { homeSection };
  }

  if (fetchError) {
    console.error(
      '[fetchHomeSectionsHandler] ❌ Erro ao buscar dados da API:',
      fetchError,
    );
  }

  if (lastGoodResponse) {
    return { homeSection: lastGoodResponse };
  }

  const fallback = (
    homeSectionsFallback as unknown as { data: HomeSectionData | HomeSection }
  ).data;

  return { homeSection: fallback };
};

type Params = undefined;

type Ports = { apiFetchHomeSections: ApiFetchHomeSectionsPort };

// Suporta tanto a nova API (HomeSectionData) quanto a antiga (HomeSection)
type Result = Promise<{ homeSection: HomeSectionData | HomeSection | null }>;
